// FocusedFrame — the PRESENTATIONAL chrome-less shell for focused flows (ADR-036 §9):
// the frame's top (the project strips slot over a slim bar — home mark + scope + a
// guarded close), a centered scrolling body bounded to ONE column, and a sticky
// footer slot — body + footer share the same max-w-2xl column so a commit bar
// lines up with the content above it.
//
// Extracted from FocusedLayout so the shell is storyable + reusable: FocusedLayout
// wraps it with the route concerns (ProtectedRoute · Outlet · project scope ·
// navigate(-1) on close · main-focus-on-navigate); a story / the capability editor
// render their page straight into it.
//
// Owns the FocusedFooter slot (context + portal): a descendant renders
// <FocusedFooter>{commit bar}</FocusedFooter> to portal its actions into the sticky
// footer region, bound to the same centered column. `empty:hidden` ⇒ a flow that
// sets no footer (the short-step create wizard) renders unchanged.

import { createContext, useContext, useEffect, useState, type ReactNode, type Ref } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LogoMark } from '../ui/Logo.js';
import { IconButton } from '../ui/IconButton.js';
import { icons } from '../ui/icons.js';
import { ShellTop } from './ShellTop.js';

const FocusedFooterContext = createContext<HTMLElement | null>(null);

export function FocusedFooter({ children }: { children: ReactNode }) {
  const target = useContext(FocusedFooterContext);
  // Bound to the SAME centered column as the body (the frame owns the width) so the
  // footer's actions align with the content above — not the viewport edge.
  return target
    ? createPortal(<div className="mx-auto w-full max-w-2xl px-4 py-3">{children}</div>, target)
    : null;
}

/** The footer-slot target (null when not under a FocusedFrame) — lets a consumer
 *  (e.g. Stepper) render its nav in the sticky footer when present, else inline. */
export function useFocusedFooter() {
  return useContext(FocusedFooterContext);
}

export interface FocusedFrameProps {
  /** Scope shown beside the home mark — a project name (string) or a wordmark node. */
  scope: ReactNode;
  /** The guarded exit — FocusedLayout passes navigate(-1); a story passes a no-op. */
  onClose: () => void;
  /** Home-link href used by the default (plain-anchor) home mark. */
  homeHref?: string;
  /** Wrap the home mark in a client-side link (FocusedLayout passes a RouterLink);
   *  omitted ⇒ a plain anchor to `homeHref`, so the shell renders without a router. */
  renderHome?: (mark: ReactNode) => ReactNode;
  /** Forwarded to <main> so a route layout can focus it on navigate. */
  mainRef?: Ref<HTMLElement>;
  /** The project-scope strips above the bar (FocusedLayout passes `ProjectStrips`
   *  — a demo / trial project says so inside a wizard too); omitted ⇒ none. */
  strips?: ReactNode;
  children: ReactNode;
}

export function FocusedFrame({
  scope,
  onClose,
  homeHref = '/',
  renderHome,
  mainRef,
  strips,
  children,
}: FocusedFrameProps) {
  const { t } = useTranslation('nav');
  const [footerEl, setFooterEl] = useState<HTMLElement | null>(null);

  // Escape exits the frame — the keyboard equivalent of the close X (both call
  // onClose, so a dirty form's route-leave guard still prompts). The
  // defaultPrevented skip lets a nested overlay (a Select popover, the discard
  // dialog) close FIRST; only a "bare" Escape exits the frame.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const homeMark = <LogoMark size={28} aria-hidden className="text-[var(--color-text-primary)]" />;

  return (
    <div className="flex h-dvh flex-col bg-[var(--color-canvas)] text-[var(--color-text-primary)]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:start-2 focus:z-tooltip focus:px-3 focus:py-2 focus:rounded-md focus:bg-[var(--color-bg-page)] focus:text-[var(--color-text-primary)] focus:border focus:border-[var(--color-border)] focus:shadow-overlay focus:outline-2 focus:outline-offset-2 focus:outline-[var(--color-primary-ring)]"
      >
        {t('skipToContent')}
      </a>
      {/* The frame's top — the strips over the bar, one measured block (lib/shellTop).
          Not pinned: this frame scrolls inside its own <main>, so the block is
          simply the column's first fixed row. */}
      <ShellTop strips={strips} pinned={false}>
        <header className="border-b border-[var(--color-border)] bg-[var(--color-chrome)]">
          {/* Full-width bar: brand flush-start, close flush-end. The page centers its
              own content; the bar spans the viewport. */}
          <div className="flex h-14 w-full items-center justify-between gap-2 px-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-2.5">
              {renderHome ? (
                renderHome(homeMark)
              ) : (
                <a
                  href={homeHref}
                  aria-label={t('homeLink')}
                  className="flex shrink-0 items-center rounded-sm outline-none focus-ring"
                >
                  {homeMark}
                </a>
              )}
              {typeof scope === 'string' ? (
                <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">{scope}</span>
              ) : (
                scope
              )}
            </div>
            <IconButton variant="ghost" aria-label={t('common:actions.close')} onClick={onClose}>
              <icons.close size={18} aria-hidden />
            </IconButton>
          </div>
        </header>
      </ShellTop>
      <FocusedFooterContext.Provider value={footerEl}>
        {/* The focused-page column — the frame owns the width so the body and the
            FocusedFooter commit bar share one centered column on every focused page. */}
        <main
          ref={mainRef}
          id="main-content"
          tabIndex={-1}
          // `relative` is load-bearing: absolute descendants (sr-only radio
          // inputs — ChoiceTiles) must resolve against THIS scroller, not the
          // document — unanchored, they extend the html past the h-dvh frame
          // and a second scroll gesture chains the whole frame off-screen
          // (the un-sticky footer bug).
          // `--dt-sticky-top: 0px`: THIS element is the scroller here, so a pinned
          // surface inside it (a thead, sticky days) lands at ITS top — the
          // frame's top above it is outside the scroll box.
          className="relative flex-1 min-h-0 min-w-0 overflow-y-auto outline-none [--dt-sticky-top:0px]"
        >
          <div className="mx-auto w-full max-w-2xl px-4 py-6">{children}</div>
        </main>
      </FocusedFooterContext.Provider>
      {/* Anchored commit-bar slot — sticky to the viewport bottom; a page fills it via
          <FocusedFooter>. empty:hidden ⇒ pages that don't set it render unchanged. */}
      <div
        ref={setFooterEl}
        className="shrink-0 border-t border-[var(--color-border)] bg-[var(--color-chrome)] empty:hidden"
      />
    </div>
  );
}
